import { z } from "zod";

/**
 * Wire-shape mirror of the shore's variable pay PLAN DTOs (PAYD-FR-010 to
 * FR-014, `variable-pay/variable-pay-plan.dto.ts` and
 * `variable-pay.vocabulary.ts`).
 *
 * The vocabularies are copied letter for letter rather than widened to
 * `z.string()`. A cadence the shore accepts and this file does not recognise
 * would render as a blank chip, and a cadence this file offers and the shore
 * rejects is a 400 the reader cannot act on. One list, both sides.
 *
 * Every money figure stays a STRING all the way to the screen: a float cannot
 * hold every two-decimal amount, and the shore already sends the rounded text
 * it wants shown.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const CURRENCY = /^[A-Z]{3}$/;
/// Shore's `@IsNumberString`: decimal text, optionally signed, no separators.
const DECIMAL = /^-?\d{1,15}(\.\d{1,6})?$/;

export const VARIABLE_PAY_CADENCES = ["monthly", "quarterly", "half_yearly", "yearly"] as const;
export type VariablePayCadence = (typeof VARIABLE_PAY_CADENCES)[number];

/// How many periods of each cadence make a year. Mirrors the shore's
/// `PERIODS_PER_YEAR`: the period target is the annual basis divided by this,
/// which is arithmetic the editor's preview has to reproduce exactly.
export const PERIODS_PER_YEAR: Readonly<Record<VariablePayCadence, number>> = {
  monthly: 12,
  quarterly: 4,
  half_yearly: 2,
  yearly: 1,
};

/// Whole months a plan may wait after its period ends before paying.
export const MAX_PAYOUT_MONTH_OFFSET = 3;

export const VARIABLE_PAY_BASIS_TYPES = [
  "fixed",
  "percent_of_annual_ctc",
  "structure_line",
  "percent_of_component",
] as const;
export type VariablePayBasisType = (typeof VARIABLE_PAY_BASIS_TYPES)[number];

/// The bases whose `basisValue` is a percent rather than an amount.
export const PERCENT_BASIS_TYPES: readonly VariablePayBasisType[] = [
  "percent_of_annual_ctc",
  "percent_of_component",
];

/// The bases that name another object in `basisRef`.
export const REFERENCED_BASIS_TYPES: readonly VariablePayBasisType[] = [
  "structure_line",
  "percent_of_component",
];

export const VARIABLE_PAY_PRORATION_MODES = [
  "none",
  "by_days_active_in_period",
  "by_payment_days",
] as const;
export type VariablePayProrationMode = (typeof VARIABLE_PAY_PRORATION_MODES)[number];

export const VARIABLE_PAY_MULTIPLIER_SOURCES = ["none", "manual", "review_rating"] as const;
export type VariablePayMultiplierSource = (typeof VARIABLE_PAY_MULTIPLIER_SOURCES)[number];

export const NO_RATING_BEHAVIOURS = ["hold", "multiplier_1", "void"] as const;
export type NoRatingBehaviour = (typeof NO_RATING_BEHAVIOURS)[number];

export const VARIABLE_PAY_TAX_TIMINGS = ["inherit", "spread", "full_in_cycle"] as const;
export const CLAWBACK_BASES = ["full", "pro_rata_remaining_months"] as const;
export const VARIABLE_PAY_EXIT_RULES = ["void", "pay_prorated_in_settlement"] as const;

export const VARIABLE_PAY_PAYOUT_SCHEDULES = ["company_period", "employee_appraisal"] as const;
export type VariablePayPayoutSchedule = (typeof VARIABLE_PAY_PAYOUT_SCHEDULES)[number];

export const VARIABLE_PAY_PLAN_STATUSES = ["draft", "active", "paused", "archived"] as const;
export type VariablePayPlanStatus = (typeof VARIABLE_PAY_PLAN_STATUSES)[number];

/// The statuses `PATCH :planId` accepts. `archived` goes through the archive
/// route, which takes a reason and says what it keeps.
export const EDITABLE_PLAN_STATUSES = ["draft", "active", "paused"] as const;

export const multiplierTableRowSchema = z
  .object({ rating: z.string(), multiplier: z.string() })
  .strict();

/**
 * FR-012. Every field optional, and `{}` means EVERYONE is eligible.
 *
 * Not `.strict()`: the shore types this as `Record<string, unknown>` on the
 * way out, so a rule key added there must not blank the plan page.
 */
export const variablePayAudienceSchema = z.object({
  departmentIds: z.array(z.string()).optional(),
  subtrees: z
    .array(
      z.object({
        subtreeRootMembershipId: z.string(),
        subtreeIncludeIndirect: z.boolean(),
        subtreeIncludeRoot: z.boolean(),
      }),
    )
    .optional(),
  includeMembershipIds: z.array(z.string()).optional(),
});

export const variablePayEligibilitySchema = z.object({
  activeOnPayoutDate: z.boolean().optional(),
  minimumServiceDays: z.number().int().optional(),
  employmentTypes: z.array(z.string()).optional(),
  audience: variablePayAudienceSchema.optional(),
});
export type VariablePayEligibility = z.infer<typeof variablePayEligibilitySchema>;

export const variablePayPlanSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    componentId: z.string(),
    cadence: z.enum(VARIABLE_PAY_CADENCES),
    payoutMonthOffset: z.number().int(),
    basisType: z.enum(VARIABLE_PAY_BASIS_TYPES),
    basisValue: z.string().nullable(),
    basisRef: z.string().nullable(),
    currency: z.string().regex(CURRENCY),
    eligibility: variablePayEligibilitySchema,
    /// The shore's own answer to "does this rule pay everyone", so the plan
    /// page states it rather than re-deriving it from an empty object.
    eligibilityIsPermissive: z.boolean(),
    prorationMode: z.enum(VARIABLE_PAY_PRORATION_MODES),
    multiplierSource: z.enum(VARIABLE_PAY_MULTIPLIER_SOURCES),
    multiplierTable: z.array(multiplierTableRowSchema),
    noRatingBehaviour: z.enum(NO_RATING_BEHAVIOURS),
    taxTiming: z.enum(VARIABLE_PAY_TAX_TIMINGS),
    clawbackMonths: z.number().int().nullable(),
    clawbackBasis: z.enum(CLAWBACK_BASES).nullable(),
    exitRule: z.enum(VARIABLE_PAY_EXIT_RULES),
    payoutSchedule: z.enum(VARIABLE_PAY_PAYOUT_SCHEDULES),
    status: z.enum(VARIABLE_PAY_PLAN_STATUSES),
    effectiveFrom: z.string().regex(ISO_DATE),
    effectiveTo: z.string().regex(ISO_DATE).nullable(),
    version: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .strict();
export type VariablePayPlan = z.infer<typeof variablePayPlanSchema>;

export const variablePayPlanListSchema = z
  .object({
    rows: z.array(variablePayPlanSchema),
    nextCursor: z.string().nullable(),
    unsentApprovedCount: z.number().int(),
  })
  .strict();
export type VariablePayPlanList = z.infer<typeof variablePayPlanListSchema>;

/**
 * Request mirror of `CreateVariablePayPlanDto`.
 *
 * The optional fields are genuinely optional on the shore, which is what lets
 * the editor ask two questions and leave the other nine at their defaults.
 */
export const createVariablePayPlanSchema = z.object({
  name: z.string().min(1).max(120),
  componentId: z.string().min(1).max(40),
  cadence: z.enum(VARIABLE_PAY_CADENCES),
  payoutMonthOffset: z.number().int().min(0).max(MAX_PAYOUT_MONTH_OFFSET).optional(),
  basisType: z.enum(VARIABLE_PAY_BASIS_TYPES),
  basisValue: z.string().regex(DECIMAL).optional(),
  basisRef: z.string().min(1).max(60).optional(),
  currency: z.string().regex(CURRENCY),
  eligibility: variablePayEligibilitySchema.optional(),
  prorationMode: z.enum(VARIABLE_PAY_PRORATION_MODES).optional(),
  multiplierSource: z.enum(VARIABLE_PAY_MULTIPLIER_SOURCES).optional(),
  multiplierTable: z.array(multiplierTableRowSchema).optional(),
  noRatingBehaviour: z.enum(NO_RATING_BEHAVIOURS).optional(),
  taxTiming: z.enum(VARIABLE_PAY_TAX_TIMINGS).optional(),
  clawbackMonths: z.number().int().min(1).max(120).optional(),
  clawbackBasis: z.enum(CLAWBACK_BASES).optional(),
  exitRule: z.enum(VARIABLE_PAY_EXIT_RULES).optional(),
  payoutSchedule: z.enum(VARIABLE_PAY_PAYOUT_SCHEDULES).optional(),
  effectiveFrom: z.string().regex(ISO_DATE),
  effectiveTo: z.string().regex(ISO_DATE).optional(),
});
export type CreateVariablePayPlanInput = z.infer<typeof createVariablePayPlanSchema>;

/// Request mirror of `UpdateVariablePayPlanDto`. `version` is the read this
/// edit changed from; the shore answers 409 when it has moved on.
export const updateVariablePayPlanSchema = createVariablePayPlanSchema.partial().extend({
  version: z.number().int(),
  status: z.enum(EDITABLE_PLAN_STATUSES).optional(),
  clawbackMonths: z.number().int().min(1).max(120).nullable().optional(),
  clawbackBasis: z.enum(CLAWBACK_BASES).nullable().optional(),
  effectiveTo: z.string().regex(ISO_DATE).nullable().optional(),
});
export type UpdateVariablePayPlanInput = z.infer<typeof updateVariablePayPlanSchema>;

export const archiveVariablePayPlanSchema = z.object({
  version: z.number().int(),
  reason: z.string().min(3).max(500),
});
export type ArchiveVariablePayPlanInput = z.infer<typeof archiveVariablePayPlanSchema>;
